import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AccountServices from '../../../services/AccountServices';
import { AlertMessagesContext } from 'react-alert-messages';
import DateTimeHelpers from '../../../helpers/DateTimeHelpers';

const LedgerDetailTable = ({
  accountHeadId,
  startDate,
  endDate,
  shiftId,
  accountHeads,
  cachedLedgers,
  setCachedLedgers,
}) => {
  const { t } = useTranslation('common');
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const [ledgerEntries, setLedgerEntries] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchLedger = useCallback(
    async (force = false) => {
      const startStr = startDate ? new Date(startDate).getTime() : 'none';
      const endStr = endDate ? new Date(endDate).getTime() : 'none';
      const cacheKey = `ledger_${accountHeadId}_${startStr}_${endStr}_${shiftId}`;

      if (!force && cachedLedgers && cachedLedgers[cacheKey]) {
        setLedgerEntries(cachedLedgers[cacheKey]);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        const params = { account_head: accountHeadId, limit: 1000 };
        if (startDate) params.start_date = DateTimeHelpers.getTimezoneOffsetDate(startDate);
        if (endDate) params.end_date = DateTimeHelpers.getTimezoneOffsetDate(endDate);
        if (shiftId && shiftId !== 'all') params.shift_id = shiftId;

        const data = await AccountServices.getAccountLedger(params);
        const results = data.results || data || [];
        setLedgerEntries(results);
        if (setCachedLedgers) {
          setCachedLedgers((prev) => ({ ...prev, [cacheKey]: results }));
        }
      } catch (err) {
        postAlertMessage({ text: err.message, type: 'failed' });
      }
      setLoading(false);
    },
    [accountHeadId, startDate, endDate, shiftId, postAlertMessage, cachedLedgers, setCachedLedgers]
  );

  useEffect(() => {
    if (accountHeadId) {
      fetchLedger();
    }
  }, [fetchLedger, accountHeadId]);

  const formatDate = (dateVal) => {
    if (!dateVal) return '';
    const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const getTransactionCategory = useCallback(
    (entry) => {
      if (!entry) return null;
      const reference = (entry.reference || '').toLowerCase().trim();
      const description = (entry.description || '').toLowerCase().trim();
      const accountName = (entry.account_head_name || '').toLowerCase().trim();
      const entryHeadId = entry.account_head;

      if (entryHeadId && Array.isArray(accountHeads)) {
        const matchedHead = accountHeads.find((h) => h.id === entryHeadId);
        if (matchedHead) {
          if (matchedHead.type === 'SUPPLIER') return { label: t('Vendors'), color: '#D97706' };
          if (matchedHead.type === 'CUSTOMER') return { label: t('Customers'), color: '#0891B2' };
        }
      }

      if (
        reference.includes('purchase return') ||
        description.includes('purchase return') ||
        accountName.includes('purchase return') ||
        reference.includes('purchase_return') ||
        description.includes('purchase_return')
      )
        return { label: t('Purchase Return'), color: '#DC2626' };

      const combinedStr = `${reference} ${description} ${accountName}`;
      if (
        combinedStr.includes('void') ||
        combinedStr.includes('sale return') ||
        combinedStr.includes('sale_return') ||
        combinedStr.includes('refund') ||
        combinedStr.includes('cancel')
      )
        return { label: t('Void / Sale Return'), color: '#EF4444' };
      if (
        reference.includes('balance transfer') ||
        description.includes('balance transfer') ||
        reference.includes('balancetransfer') ||
        description.includes('balancetransfer')
      )
        return { label: t('Balance Transfer'), color: '#7C3AED' };

      if (
        entry.type === 'in' ||
        entry.type === 'out' ||
        combinedStr.includes('cash in') ||
        combinedStr.includes('cash_in') ||
        combinedStr.includes('cash out') ||
        combinedStr.includes('cash_out') ||
        combinedStr.includes('owner investment') ||
        combinedStr.includes('drawings by owner') ||
        combinedStr.includes('cash deposit') ||
        combinedStr.includes('petty cash') ||
        combinedStr.includes('withdrawal') ||
        combinedStr.includes('deposit')
      )
        return { label: t('Deposit / Withdrawal'), color: '#475569' };

      if (combinedStr.includes('purchase')) return { label: t('Purchase'), color: '#2563EB' };
      if (combinedStr.includes('sale')) return { label: t('Sales'), color: '#22C55E' };
      if (combinedStr.includes('transfer')) return { label: t('Transfer'), color: '#7C3AED' };
      if (combinedStr.includes('expense')) return { label: t('Expenses'), color: '#F59E0B' };
      if (combinedStr.includes('vendor') || combinedStr.includes('supplier'))
        return { label: t('Vendors'), color: '#D97706' };
      if (combinedStr.includes('customer') || combinedStr.includes('client'))
        return { label: t('Customers'), color: '#0891B2' };

      return null;
    },
    [accountHeads, t]
  );

  const displayEntries = React.useMemo(() => {
    let baseEntries = [...ledgerEntries];
    baseEntries.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      if (dateA - dateB !== 0) return dateA - dateB;
      return a.id - b.id;
    });

    return baseEntries;
  }, [ledgerEntries]);

  const periodOpeningBalance =
    displayEntries.length > 0
      ? displayEntries[0].balance - (Number(displayEntries[0].debit || 0) - Number(displayEntries[0].credit || 0))
      : 0;

  const periodClosingBalance = displayEntries.length > 0 ? displayEntries[displayEntries.length - 1].balance : 0;

  return (
    <TableContainer sx={{ boxShadow: 'none', borderRadius: 0, backgroundColor: 'transparent' }}>
      <Table sx={{ minWidth: 650 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 2 }}>
              {t('Date')}
            </TableCell>
            <TableCell sx={{ fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 2 }}>
              {t('Description')}
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 2 }}
              align="right"
            >
              {t('Debit')}
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 2 }}
              align="right"
            >
              {t('Credit')}
            </TableCell>
            <TableCell
              sx={{ fontWeight: 600, color: '#64748b', borderBottom: '2px solid #e2e8f0', py: 2 }}
              align="right"
            >
              {t('Balance')}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748b' }}>
                {t('Loading...')}
              </TableCell>
            </TableRow>
          ) : displayEntries.length === 0 ? (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4, color: '#64748b' }}>
                {t('No Ledger Entries Found')}
              </TableCell>
            </TableRow>
          ) : (
            <>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ py: 1.5, color: '#475569' }}>{formatDate(startDate)}</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5, color: '#334155' }}>{t('Opening Balance')}</TableCell>
                <TableCell align="right" sx={{ py: 1.5, color: '#475569' }}>
                  -
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, color: '#475569' }}>
                  -
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, color: '#0f172a' }}>
                  {periodOpeningBalance.toFixed(2)}
                </TableCell>
              </TableRow>
              {displayEntries.map((entry) => {
                const category = getTransactionCategory(entry);
                return (
                  <TableRow key={entry.id} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell sx={{ py: 1.5, color: '#475569' }}>{formatDate(entry.date, entry)}</TableCell>
                    <TableCell sx={{ py: 1.5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        {category && (
                          <span
                            style={{
                              width: '8px',
                              height: '8px',
                              borderRadius: '50%',
                              backgroundColor: category.color,
                              display: 'inline-block',
                              flexShrink: 0,
                            }}
                            title={category.label}
                          />
                        )}
                        <span style={{ color: '#334155' }}>{entry.description || entry.reference || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.5, color: '#059669', fontWeight: 500 }}>
                      {Number(entry.debit).toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.5, color: '#e11d48', fontWeight: 500 }}>
                      {Number(entry.credit).toFixed(2)}
                    </TableCell>
                    <TableCell align="right" sx={{ py: 1.5, color: '#0f172a', fontWeight: 600 }}>
                      {entry.balance !== null ? Number(entry.balance).toFixed(2) : '-'}
                    </TableCell>
                  </TableRow>
                );
              })}
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell sx={{ py: 1.5, color: '#475569' }}>{formatDate(endDate)}</TableCell>
                <TableCell sx={{ fontWeight: 600, py: 1.5, color: '#334155' }}>{t('Closing Balance')}</TableCell>
                <TableCell align="right" sx={{ py: 1.5, color: '#475569' }}>
                  -
                </TableCell>
                <TableCell align="right" sx={{ py: 1.5, color: '#475569' }}>
                  -
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 700, py: 1.5, color: '#0f172a' }}>
                  {periodClosingBalance.toFixed(2)}
                </TableCell>
              </TableRow>
            </>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default LedgerDetailTable;
